import React, { useState } from 'react';
import ListView from '../listComponent/ListView';
import ListItem from '../listComponent/ListItem';
import Panel from '../layout/Panel';
import TextUtils from '../../classes/utilities/TextUtils';
import Spacer from '../layout/Spacer';
import Dialog from '../layout/Dialog';
import { LayoutOrientations } from '../../globals';
import TimeUtils from '../../classes/utilities/TimeUtils';
import MessagePrompter, { SpecificErrors } from '../prompts/MessagePrompter';
import ModalManager from '../layout/ModalManager';
import APIUtility from '../../classes/APIUtility';
import { ISaveServerResponse, Save } from "../../interfaces/Responses";
import { IProjectInfo } from '../../interfaces/ScenarioElements';
import ExportProjectDialog from '../importexport/ExportProjectDialog';
import { openSaveFile } from '../../classes/utilities/ProjectUtils';
import StandbyOverlay from '../layout/StandbyOverlay';

type ProjectSaveSelectorProps = {
	onSaveFileConfirmed: (save: Save) => void;
	onExportFileConfirmed: (save: Save) => void;
	saves: ISaveServerResponse["saves"];
	onShutdown?: () => void;
}

export function chooseSaveFile(projectId: string, projectInfo?: IProjectInfo)
{
	StandbyOverlay.startStandbyMode();
	//console.time("time to load saves");
	
	if(projectInfo)
	{
		showSaveList(projectInfo);
	}
	else
	{
		// try to find the project from the server
		APIUtility.getProject(projectId)
			.then((data: { project: IProjectInfo }) =>
			{
				//console.log(data)
				const projectInfo = data.project;
				if(projectInfo)
				{
					showSaveList(projectInfo);
				}
			})
			.catch((error) =>
			{
				console.error("Failed to retrieve project list");
				console.error(error);
				StandbyOverlay.stopStandbyMode();
			});
	}

	function showSaveList(project: IProjectInfo)
	{
		APIUtility.loadSaves(project._id, 100)
			.then((data) =>
			{
				StandbyOverlay.stopStandbyMode();
				//console.timeEnd("time to load saves");

				//console.log(data);
				const projectData = project;
				if (projectData)
				{
					if (data)
					{
						const modalId = ModalManager.addModal({
							content: <ProjectSaveSelector
								saves={data.saves}
								onSaveFileConfirmed={(save: Save) =>
								{
									openSaveFile(save, projectData);
								}}
								onExportFileConfirmed={(save: Save) =>
								{
									if (!save)
									{
										MessagePrompter.showError(SpecificErrors.Error20003_ScenarioLoadFailed);
										return;
									}

									ModalManager.removeModal(modalId);
									ModalManager.addModal({ content: <ExportProjectDialog revision={save} /> });
								}}
							/>
						});
					}
				}
			})
			.catch((error) =>
			{
				console.error("Failed to load project saves for " + project._id);
				console.error(error);
				StandbyOverlay.stopStandbyMode();
			});
	}
}

export function ProjectSaveSelector(props: ProjectSaveSelectorProps)
{
	const [selectedSaveFile, setSelectedSaveFile] = useState<any>(null);

	function onSaveSelected(selectionSet)
	{
		const save = selectionSet.values().next().value;
		setSelectedSaveFile(save);
	}

	function RenderSaveLists()
	{
		const output = new Array<JSX.Element>();

		const listCount = Object.keys(props.saves).length;
		for (const key of Object.keys(props.saves))
		{
			const list = props.saves[key];
			if (list.length)
			{
				output.push(
					<Panel
						key={key}
						style={{
							maxWidth: (500 / listCount) + "px",
						}}
					>
						{TextUtils.prettifyText(key + "Saves")}
						<Spacer size={{ y: "4px" }} />
						<ListView
							selectedItems={new Set().add(selectedSaveFile ? selectedSaveFile.nodeId : undefined)}
							selectionChangedCallback={onSaveSelected}
							dndDefaults={{
								isDraggable: false,
								isDroppable: false,
							}}
						>
							{
								list.slice(0).reverse().map((save: Save, idx: number) =>
								{
									return (
										<ListItem
											key={idx}
											nodeId={save.creationDate}
											label={TimeUtils.getDateFromGMTString(save.creationDate)}
											decorators={[
												<span className='createVersion'>{save.createVersion}</span>
											]}
											saveData={save}
										/>
									);
								})
							}
						</ListView>
					</Panel>
				);

				output.push(
					<Spacer key={key + "1"} size={{ x: "8px" }} />
				)
			}
		}

		output.pop();

		return output;
	}

	return (
		<Dialog data-name={"save-select-dialog"}
			title={"Select Save to Open"}
			onShutdown={props.onShutdown}
			actions={[
				{
					label: "Confirm",
					onClick: function ()
					{
						if (props.onSaveFileConfirmed && selectedSaveFile)
						{
							props.onSaveFileConfirmed(selectedSaveFile.saveData);
						}
					},
					disabled: !selectedSaveFile,
				},
				{
					label: "Export",
					onClick: function ()
					{
						if (props.onExportFileConfirmed && selectedSaveFile)
						{
							props.onExportFileConfirmed(selectedSaveFile.saveData);
						}
					},
					disabled: !selectedSaveFile,
				}
			]}
		>
			<Panel
				orientation={LayoutOrientations.Horizontal}
				style={{
					maxWidth: "750px",
					maxHeight: "450px",
				}}
			>
				<RenderSaveLists />
			</Panel>
		</Dialog>
	);
}
